import axios from 'axios';
import { fabric } from 'fabric';
import { cloneDeep } from 'lodash';
import '../fabric_obj/containerObj';
import { generateId, getViewportTransform } from './canvasHelper';
import {
  ACTIVE_SELECTION,
  CHART_MENU,
  GROUP,
  IMAGE,
  IMAGE_HTML_ID,
  IMAGE_HTML_TYPE,
  ITEXT,
  MAX_MEMORY_STACK_USER_SETTING,
  TEXTBOX,
  TEXT_GROUP_ID,
} from './constants';

export const importAll = (r, hasDefault) =>
  r.keys().map((e) => {
    const path = e.split('/');
    return {
      name: path.slice(-1)[0].split('.')[0].toUpperCase(),
      key: path.reverse()[1],
      data: hasDefault ? r(e).default : r(e),
    };
  });
export const getFonts = () =>
  new Set(
    [
      // Windows 10
      'Arial',
      'Arial Black',
      'Bahnschrift',
      'Calibri',
      'Cambria',
      'Cambria Math',
      'Candara',
      'Comic Sans MS',
      'Consolas',
      'Constantia',
      'Corbel',
      'Courier New',
      'Ebrima',
      'Franklin Gothic Medium',
      'Gabriola',
      'Gadugi',
      'Georgia',
      'HoloLens MDL2 Assets',
      'Impact',
      'Ink Free',
      'Javanese Text',
      'Leelawadee UI',
      'Lucida Console',
      'Lucida Sans Unicode',
      'Malgun Gothic',
      'Marlett',
      'Microsoft Himalaya',
      'Microsoft JhengHei',
      'Microsoft New Tai Lue',
      'Microsoft PhagsPa',
      'Microsoft Sans Serif',
      'Microsoft Tai Le',
      'Microsoft YaHei',
      'Microsoft Yi Baiti',
      'MingLiU-ExtB',
      'Mongolian Baiti',
      'MS Gothic',
      'MV Boli',
      'Myanmar Text',
      'Nirmala UI',
      'Palatino Linotype',
      'Segoe MDL2 Assets',
      'Segoe Print',
      'Segoe Script',
      'Segoe UI',
      'Segoe UI Historic',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'SimSun',
      'Sitka',
      'Sylfaen',
      'Symbol',
      'Tahoma',
      'Times New Roman',
      'Trebuchet MS',
      'Verdana',
      'Webdings',
      'Wingdings',
      'Yu Gothic',
      // macOS
      'American Typewriter',
      'Andale Mono',
      'Arial',
      'Arial Black',
      'Arial Narrow',
      'Arial Rounded MT Bold',
      'Arial Unicode MS',
      'Avenir',
      'Avenir Next',
      'Avenir Next Condensed',
      'Baskerville',
      'Big Caslon',
      'Bodoni 72',
      'Bodoni 72 Oldstyle',
      'Bodoni 72 Smallcaps',
      'Bradley Hand',
      'Brush Script MT',
      'Chalkboard',
      'Chalkboard SE',
      'Chalkduster',
      'Charter',
      'Cochin',
      'Comic Sans MS',
      'Copperplate',
      'Courier',
      'Courier New',
      'Didot',
      'DIN Alternate',
      'DIN Condensed',
      'Futura',
      'Geneva',
      'Georgia',
      'Gill Sans',
      'Helvetica',
      'Helvetica Neue',
      'Herculanum',
      'Hoefler Text',
      'Impact',
      'Lucida Grande',
      'Luminari',
      'Marker Felt',
      'Menlo',
      'Microsoft Sans Serif',
      'Monaco',
      'Noteworthy',
      'Optima',
      'Palatino',
      'Papyrus',
      'Phosphate',
      'Rockwell',
      'Savoye LET',
      'SignPainter',
      'Skia',
      'Snell Roundhand',
      'Tahoma',
      'Times',
      'Times New Roman',
      'Trattatello',
      'Trebuchet MS',
      'Verdana',
      'Zapfino',
    ].sort(),
  );
export const comparePoint = (point1, point2) => {
  const ebs = 0.00000001;
  if (Math.abs(point1.x - point2.x) < ebs && Math.abs(point1.y - point2.y) < ebs) return true;
  return false;
};
export const generateImageHtmlId = (id) => {
  const newId = `${generateId()}_${IMAGE_HTML_ID}`;
  if (id.includes(IMAGE_HTML_TYPE.radiusCheck)) return `${newId}_${IMAGE_HTML_TYPE.radiusCheck}`;
  if (id.includes(IMAGE_HTML_TYPE.boxCheck)) return `${newId}_${IMAGE_HTML_TYPE.boxCheck}`;
  if (id.includes(IMAGE_HTML_TYPE.bulletList)) return `${newId}_${IMAGE_HTML_TYPE.bulletList}`;
  if (id.includes(IMAGE_HTML_TYPE.linkBar)) return `${newId}_${IMAGE_HTML_TYPE.linkBar}`;
  if (id.includes(IMAGE_HTML_TYPE.breadcrumb)) return `${newId}_${IMAGE_HTML_TYPE.breadcrumb}`;
  if (id.includes(IMAGE_HTML_TYPE.treeList)) return `${newId}_${IMAGE_HTML_TYPE.treeList}`;
  if (id.includes(IMAGE_HTML_TYPE.table)) return `${newId}_${IMAGE_HTML_TYPE.table}`;
  if (id.includes(IMAGE_HTML_TYPE.stepNav)) return `${newId}_${IMAGE_HTML_TYPE.stepNav}`;
  if (id.includes(IMAGE_HTML_TYPE.rating)) return `${newId}_${IMAGE_HTML_TYPE.rating}`;
  if (id.includes(IMAGE_HTML_TYPE.calendar)) return `${newId}_${IMAGE_HTML_TYPE.calendar}`;
  if (id.includes(IMAGE_HTML_TYPE.menuBar)) return `${newId}_${IMAGE_HTML_TYPE.menuBar}`;

  if (id.includes(IMAGE_HTML_TYPE.chart)) return `${newId}_${IMAGE_HTML_TYPE.chart}`;

  return id;
};
export const hasImageInObject = (object) => {
  if (!object) return false;
  if (object.type === IMAGE && object.imageId) return true;
  if (object.getObjects) {
    return object.getObjects()?.some((obj) => hasImageInObject(obj));
  }
  return false;
};
export const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

export const getCategory = (categories, components, searchValue) => {
  const filterBySearchValue = (component) => component.name.toLowerCase().includes(searchValue.toLowerCase());
  let result = cloneDeep(categories);
  if (searchValue && searchValue.trim() !== '') {
    result = result
      .map((category) => {
        if (category.subCategoryList) {
          return {
            ...category,
            subCategoryList: category.subCategoryList
              .map((subCate) => ({
                ...subCate,
                componentList: subCate.componentList.filter(filterBySearchValue),
              }))
              .filter((subCategory) => subCategory.componentList?.length > 0),
          };
        } else {
          return {
            ...category,
            componentList: category.componentList.filter(filterBySearchValue),
          };
        }
      })
      .filter((category) => {
        if (category.subCategoryList) return category.subCategoryList?.length > 0;
        else return category.componentList?.length > 0;
      });
    if (result.length === 0) {
      result = categories.map((component) => {
        if (component.subCategoryList)
          return {
            ...component,
            subCategoryList: [],
          };
        else
          return {
            ...component,
            componentList: [],
          };
      });
    }
  } else {
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      if (category.subCategoryList) {
        category.subCategoryList = category.subCategoryList.map((subCate) => ({
          ...subCate,
          componentList: components[subCate.categoryName]?.[subCate.subCategoryName]?.filter(filterBySearchValue) || [],
        }));
      } else {
        category.componentList = components[category.categoryName]?.filter(filterBySearchValue) || [];
      }
      result[i] = category;
    }
  }
  return result;
};

export const getPanelOpenValues = (category) => {
  let openedPanels = [],
    openedSubPanels = [];
  category.map((item) => {
    openedPanels = [...openedPanels, item.groupId];
    if (item.subCategoryList) {
      openedSubPanels = [...openedSubPanels, ...item.subCategoryList.map((subCate) => subCate.groupId)];
    }
  });
  return { openedPanels, openedSubPanels };
};

export const hasImageInData = (data) => {
  if (data.type === IMAGE && data.imageId) return true;
  return data.objects?.some((obj) => hasImageInData(obj));
};
export const getUpdatedViewPortList = (userSettings, currentPage) => {
  let updateListViewPort = userSettings.viewPortList;
  const { translationX, translationY, scaleX, scaleY } = getViewportTransform();
  const isUpdate = updateListViewPort.some((page) => {
    if (page.id === currentPage) return (page.viewPort = [scaleX, 0, 0, scaleY, translationX, translationY]);
  });
  if (!isUpdate && currentPage)
    if (updateListViewPort.length === MAX_MEMORY_STACK_USER_SETTING) {
      updateListViewPort = [
        ...updateListViewPort.slice(1),
        { id: currentPage, viewPort: [scaleX, 0, 0, scaleY, translationX, translationY] },
      ];
    } else {
      updateListViewPort = [
        ...updateListViewPort,
        { id: currentPage, viewPort: [scaleX, 0, 0, scaleY, translationX, translationY] },
      ];
    }
  return updateListViewPort;
};

export const capitalizeFirstChar = (name) => {
  return `${name && name.charAt(0).toUpperCase() + name.slice(1)}`;
};

export const getOCoordsBasedOnBoundingBox = (oldObj) => {
  if (!oldObj) return;
  oldObj.setCoords();
  const obj = cloneDeep(oldObj);
  let oCoords = oldObj.oCoords;
  let groupItem = null;
  const { canvas } = window;
  if (obj.isTextComponent || obj.isCustomText) {
    groupItem = canvas.getObjects().find((o) => o.id === obj.parentId);
  } else if (obj?.parentId?.includes(TEXT_GROUP_ID)) {
    groupItem = canvas.getObjects().find((o) => o.parentId === obj.parentId && ![TEXTBOX, ITEXT].includes(o.type));
  } else if (
    [TEXTBOX, ITEXT].includes(obj.type) ||
    obj?.id?.includes(IMAGE_HTML_ID) ||
    obj?.isDiagram ||
    obj.type === ACTIVE_SELECTION ||
    obj.type === GROUP
  ) {
    groupItem = obj;
  }
  if (groupItem) {
    const group = new fabric.Group([groupItem]);
    canvas.add(group);
    oCoords = group.oCoords;
    group.destroy();
    canvas.remove(group);
    canvas.renderAll();
  }
  return oCoords;
};

export const RGBAToHex = (rgba, forceRemoveAlpha = false) => {
  return (
    '#' +
    rgba
      .replace(/^rgba?\(|\s+|\)$/g, '')
      .split(',')
      .filter((_, index) => !forceRemoveAlpha || index !== 3)
      .map((string) => parseFloat(string))
      .map((number, index) => (index === 3 ? Math.round(number * 255) : number))
      .map((number) => number.toString(16))
      .map((string) => (string.length === 1 ? '0' + string : string))
      .join('')
  );
};

export const hexToRGBA = (hex, opacity) => {
  var r = parseInt(hex.slice(1, 3), 16);
  var g = parseInt(hex.slice(3, 5), 16);
  var b = parseInt(hex.slice(5, 7), 16);

  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + opacity + ')';
};

export const toCamelCase = (str) => {
  const firstChar = str.charAt(0).toLowerCase();
  const camelCase = firstChar + str.slice(1);
  return camelCase;
};

export const getSuitableColorByHex = (hexColor) => {
  hexColor = hexColor.replace('#', '');

  const red = parseInt(hexColor.substr(0, 2), 16);
  const green = parseInt(hexColor.substr(2, 2), 16);
  const blue = parseInt(hexColor.substr(4, 2), 16);

  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

  const threshold = 0.5;

  if (luminance > threshold) {
    return '#000';
  } else {
    return '#fff';
  }
};

export const getChartOptions = (data) => {
  const { width, height, padding, categories, series, type, legend, grid, xAxis, yAxis, rAxis, plot, selectedColumn } =
    data;

  const title = CHART_MENU[type].title.toLowerCase();

  const isStacked = title.includes('stacked');

  const isBarChart = title.includes('bar');
  const isColumnChart = title.includes('column');
  const isAreaChart = title.includes('area');
  const isPieChart = title.includes('pie');
  const isPolarChart = title.includes('polar');
  const isRadarChart = title.includes('radar');

  const isLineOrRadarChart = ['line', 'radar'].some((keyword) => title.includes(keyword));
  const isPieOrPolarChart = ['pie', 'polar'].some((keyword) => title.includes(keyword));
  const isPolarOrRadarChart = ['polar', 'radar'].some((keyword) => title.includes(keyword));

  const isPieOrPolarOrRadarChart = ['pie', 'polar', 'radar'].some((keyword) => title.includes(keyword));
  const isAreaOrLineOrRadarChart = ['area', 'line', 'radar'].some((keyword) => title.includes(keyword));

  let options = {
    width,
    height,
    labels: categories,
    options: {
      layout: {
        padding: { top: padding.top, left: padding.left, right: padding.right, bottom: padding.bottom },
      },
      animation: false,
      responsiveAnimationDuration: 0,
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: legend.display,
          position: legend.position,
          labels: {
            boxWidth: 10,
            generateLabels: ({ data }) => {
              if (isPieOrPolarChart) {
                return data.labels.map((label, index) => {
                  const dataset = data.datasets[0];
                  return {
                    text: label,
                    fillStyle: dataset.backgroundColor[index],
                    lineWidth: 0,
                    hidden: false,
                    index,
                  };
                });
              } else {
                return data.datasets.map((item, index) => {
                  return {
                    text: item.label,
                    fillStyle: item.backgroundColor,
                    lineWidth: 0,
                    hidden: false,
                    index,
                  };
                });
              }
            },
          },
        },
        datalabels: {
          display: plot.values.display,
          align: plot.values.align.toLowerCase(),
          formatter: (v, context) => {
            if (isAreaChart && isStacked && v === 0) return '';
            if (!plot.percentage) return;
            let col, value;
            if (isPieOrPolarChart) {
              col = context.dataset.data;
              value = col[context.dataIndex];
            } else {
              const row = series[context.datasetIndex].data;
              col = series.map((s) => s.data[context.dataIndex]);
              value = row[context.dataIndex];
            }
            const total = col.reduce((acc, data) => acc + data, 0);
            const percentage = ((value / total) * 100).toFixed(0) + '%';
            return percentage;
          },
        },
      },
    },
  };

  if (!isPieOrPolarOrRadarChart) {
    options = {
      ...options,
      options: {
        ...options.options,
        scales: {
          x: {
            grid: {
              display: grid.display,
              color: grid.color,
              lineWidth: grid.lineWidth,
              drawTicks: xAxis.drawTicks,
            },
            border: {
              dash: grid.dash.value,
              display: xAxis.borderDisplay,
              color: xAxis.color,
              width: xAxis.lineWidth,
            },
            display: xAxis.display,
            position: xAxis.position,
            ticks: {
              display: xAxis.ticksDisplay,
              padding: 4,
            },
            beginAtZero: true,
          },
          y: {
            grid: {
              display: grid.display,
              color: grid.color,
              lineWidth: grid.lineWidth,
              drawTicks: yAxis.drawTicks,
            },
            border: {
              dash: grid.dash.value,
              display: yAxis.borderDisplay,
              color: yAxis.color,
              width: yAxis.lineWidth,
            },
            display: yAxis.display,
            position: yAxis.position,
            ticks: {
              display: yAxis.ticksDisplay,
              padding: 4,
            },
            beginAtZero: true,
          },
        },
      },
    };
  }

  if (isPolarOrRadarChart) {
    options = {
      ...options,
      options: {
        ...options.options,
        scales: {
          r: {
            grid: {
              display: rAxis.display,
              color: rAxis.color,
              lineWidth: rAxis.lineWidth,
            },
            ticks: {
              display: rAxis.drawTicks,
            },
            pointLabels: {
              display: isPolarChart ? false : rAxis.ticksDisplay,
            },
            angleLines: {
              color: rAxis.color,
            },
          },
        },
      },
    };
  }

  if (isBarChart) {
    options = {
      ...options,
      datasets: series.map((s) => ({
        label: s.name,
        data: s.data,
        backgroundColor: s.color,
        datalabels: {
          color: plot.values.color === 'Auto' ? getSuitableColorByHex(s.color) : s.color,
        },
        barPercentage: plot.bar.thickness / 100,
        ...CHART_MENU[type].options,
      })),
      options: {
        ...options.options,
        indexAxis: 'y',
      },
    };
  }

  if (isColumnChart) {
    options = {
      ...options,
      datasets: series.map((s) => ({
        label: s.name,
        data: s.data,
        backgroundColor: s.color,
        datalabels: {
          color: plot.values.color === 'Auto' ? getSuitableColorByHex(s.color) : s.color,
        },
        barPercentage: plot.bar.thickness / 100,
        ...CHART_MENU[type].options,
      })),
    };
  }

  if (isAreaChart) {
    options = {
      ...options,
      datasets: series.map((s) => ({
        label: s.name,
        data: s.data,
        borderColor: s.color,
        backgroundColor: isStacked ? s.color : hexToRGBA(s.color, 0.5),
        pointBackgroundColor: s.color,
        datalabels: {
          color: plot.values.color === 'Auto' ? getSuitableColorByHex(s.color) : s.color,
        },
        fill: true,
        ...CHART_MENU[type].options,
      })),
      options: {
        ...options.options,
        elements: {
          point: {
            pointStyle: toCamelCase(plot.pointStyle),
            radius: isStacked ? 0 : plot.pointRadius,
            borderWidth: isStacked ? 0 : plot.pointBorderWidth,
          },
        },
      },
    };

    if (isStacked) {
      options.options.scales.y.stacked = true;
    }
  }

  if (isLineOrRadarChart) {
    options = {
      ...options,
      datasets: series.map((s) => ({
        label: s.name,
        data: s.data,
        borderColor: s.color,
        backgroundColor: isRadarChart ? hexToRGBA(s.color, 0.5) : s.color,
        datalabels: {
          color: plot.values.color === 'Auto' ? getSuitableColorByHex(s.color) : s.color,
        },
        borderWidth: plot.lineStyle.lineWidth,
        ...CHART_MENU[type].options,
      })),
      options: {
        ...options.options,
        elements: {
          point: {
            pointStyle: toCamelCase(plot.pointStyle),
            radius: plot.pointRadius,
            borderWidth: plot.pointBorderWidth,
          },
        },
      },
    };
  }

  if (isPieOrPolarChart) {
    options = {
      ...options,
      labels: series.map((s) => s.name),
      datasets: [
        {
          data: series.map((s) => s.data[selectedColumn]),
          borderColor: isPolarChart ? series.map((s) => s.color) : '#fff',
          backgroundColor: series.map((s) => (isPolarChart ? hexToRGBA(s.color, 0.5) : s.color)),
          datalabels: {
            color: (context) => {
              const dataIndex = context.dataIndex;
              return plot.values.color === 'Auto'
                ? getSuitableColorByHex(series[dataIndex].color)
                : series[dataIndex].color;
            },
          },
          borderWidth: 0,
          spacing: isPieChart ? plot.pie.spacing : 0,
          borderRadius: isPieChart ? plot.pie.borderRadius / 5 : 0,
          cutout: isPieChart ? `${plot.pie.cutout}` : 0,
        },
      ],
      ...CHART_MENU[type].options,
    };
  }

  if (isAreaOrLineOrRadarChart) {
    options.options.plugins.datalabels.display = (context) => {
      if (!plot.values.display) return false;
      const row = series[context.datasetIndex].data;
      const col = series.map((s) => s.data[context.dataIndex]);
      const value = row[context.dataIndex];
      const count = col.reduce((acc, curr) => acc + (curr === value), 0);
      if (count > 1 && context.datasetIndex !== 0) return 'auto';
      return true;
    };
    options.options.tension = plot.tension;
  }

  if (isStacked && plot.values.display && plot.percentage) {
    options.options.plugins.stacked100 = {
      enable: true,
      precision: 1,
    };
    options.options.plugins.datalabels = {
      align: plot.values.align.toLowerCase(),
      formatter: (value) => {
        if (isAreaChart && value === 0) return '';
        return Math.round(value) + '%';
      },
    };
  }

  return options;
};

export const getRandomColor = () => {
  const n = (Math.random() * 0xfffff * 1000000).toString(16);
  return `#${n.slice(0, 6)}`;
};

export const getAxios = async (url) => {
  const { data } = await axios.get(url);
  return data;
};

export const isDevelop = () => process.env.NODE_ENV === 'development';

export const checkBucketUrl = (url) => (isDevelop() ? url : url.replace('mocky-data-dev', 'mocky-data'));
